import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { customerAPI, broadbandAPI, catvAPI, configAPI, serviceRequestAPI } from '../../services/api';
import toast from 'react-hot-toast';
import { Check, PenLine, FileText } from 'lucide-react';
import SignaturePad from '../../components/agent/SignaturePad';
import PaymentStep from '../../components/customer/PaymentStep';
import SearchableSelect from '../../components/common/SearchableSelect';
import DtdPlanChangeNotice from '../../components/customer/DtdPlanChangeNotice';

const fmt = n => new Intl.NumberFormat('en-IN').format(Math.round(n || 0));

export default function CustomerPlansPage() {
  const [searchParams] = useSearchParams();
  const [customer, setCustomer] = useState(null);
  const [plans, setPlans] = useState([]);
  const [packages, setPackages] = useState([]);
  const [config, setConfig] = useState({ upiEntries: [], upiName: 'AngelNet', catvUpiEntries: [], catvUpiName: 'AngelNet' });
  const [loading, setLoading] = useState(true);
  const [serviceType, setServiceType] = useState('broadband');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedBbPlanId, setSelectedBbPlanId] = useState(null);
  const [signOpen, setSignOpen] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // { ticketNumber, plan }
  const [dtdNoticeOpen, setDtdNoticeOpen] = useState(false);

  useEffect(() => {
    Promise.all([
      customerAPI.getMe(),
      broadbandAPI.getPlans({ activeOnly: 'true' }),
      catvAPI.getPackages({ activeOnly: 'true' }),
      configAPI.get(),
    ]).then(([meRes, bbRes, catvRes, cfgRes]) => {
      const me = meRes.data.data;
      setCustomer(me);
      setPlans(bbRes.data?.data || []);
      setPackages(catvRes.data?.data || []);
      if (cfgRes.data?.data) setConfig(cfgRes.data.data);

      // Combo (CableTV + Broadband) customers only ever see broadband here — business
      // rule, 2026-07-27, matches Online PG's combo accounts always routing through the
      // Broadband gateway. Computed straight off `me` since hasBB/hasCatv below aren't
      // available until after this effect (they depend on `customer` state).
      const meLinked = me.linkedAccount || null;
      const meBbSource = me.customerType !== 'catv' ? me : meLinked;
      const meCatvSource = me.customerType !== 'broadband' ? me : meLinked;
      const meHasBB = (meBbSource?.customerType === 'broadband' || meBbSource?.customerType === 'both') && !!meBbSource?.broadband;
      const meHasCatv = (meCatvSource?.customerType === 'catv' || meCatvSource?.customerType === 'both') && !!meCatvSource?.catv;

      const requested = searchParams.get('service');
      if (meHasBB && meHasCatv) {
        setServiceType('broadband');
      } else if (requested === 'broadband' || requested === 'catv') {
        setServiceType(requested);
      } else {
        setServiceType(me.customerType === 'catv' ? 'catv' : 'broadband');
      }
    }).catch(() => toast.error('Failed to load plans'))
      .finally(() => setLoading(false));
  }, [searchParams]);

  if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>;
  if (!customer) return null;

  const linked = customer.linkedAccount || null;
  const bbSource = customer.customerType !== 'catv' ? customer : linked;
  const catvSource = customer.customerType !== 'broadband' ? customer : linked;

  const account = serviceType === 'catv' ? catvSource : bbSource;
  // Add-ons (e.g. channel/OTT packs layered on top of a base package) aren't a standalone
  // switchable plan — only base/ala-carte CATV packages, and all broadband plans, belong here.
  const items = serviceType === 'broadband' ? plans : packages.filter(p => p.type !== 'addon');
  const currentId = serviceType === 'broadband'
    ? bbSource?.broadband?.plan?._id
    : catvSource?.catv?.package?._id;

  const handleChoose = async (item) => {
    // Browsing plans for a service this login doesn't have yet — there's no account
    // to raise a package_change against, so treat it as a new-service lead instead
    // (WhatsApps the admins, same as the dashboard's "Request Today" card).
    if (!account) {
      try {
        const { data } = await customerAPI.requestServiceInterest(serviceType);
        toast.success(data.message || 'Request sent — our team will contact you soon!');
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to send request');
      }
      return;
    }
    if (serviceType === 'catv' && account.billing?.durationType === 'dtd') {
      setDtdNoticeOpen(true);
      return;
    }
    setSelectedPlan(item);
    setSignOpen(true);
  };

  const handleSign = async (signature) => {
    if (!signature || !selectedPlan || !account) return;
    try {
      const payload = {
        customer: account._id,
        type: 'package_change',
        serviceType,
        signature,
        reason: 'Customer self-service plan selection via portal',
      };
      if (serviceType === 'broadband') payload.requestedPlan = selectedPlan._id;
      else payload.requestedPackage = selectedPlan._id;

      const { data } = await serviceRequestAPI.create(payload);
      toast.success(`Request ${data.data.ticketNumber} submitted!`);
      setConfirmed({ ticketNumber: data.data.ticketNumber, plan: selectedPlan });
      setSignOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit request');
    }
  };

  // One card per plan — used both by CATV's full grid and by Broadband's single
  // selected-plan view below, so plan details/actions can't drift between the two.
  function renderPlanCard(item) {
    const isCurrent = item._id === currentId;
    const isCatv = serviceType === 'catv';
    const accent = isCatv ? '#7c3aed' : '#2563eb';
    const genres = isCatv
      ? ['sports', 'movies', 'news', 'kids', 'regional', 'international'].filter(g => item.includes?.[g])
      : [];
    return (
      <div key={item._id} style={{
        position: 'relative', display: 'flex', flexDirection: 'column',
        background: 'var(--bg-card, #fff)', borderRadius: 18, overflow: 'hidden',
        border: isCurrent ? `2px solid ${accent}` : '1px solid var(--border)',
        boxShadow: isCurrent ? `0 10px 26px -12px ${accent}66` : '0 4px 14px -8px rgba(15,23,42,.14)',
      }}>
        {/* Colored accent strip with badges */}
        <div style={{ height: 5, background: `linear-gradient(90deg, ${accent}, ${isCatv ? '#c084fc' : '#38bdf8'})` }} />
        {(isCurrent || item.isPopular) && (
          <span style={{
            position: 'absolute', top: 14, right: 14, fontSize: '.66rem', fontWeight: 800,
            letterSpacing: '.04em', textTransform: 'uppercase', padding: '4px 10px', borderRadius: 99,
            background: isCurrent ? '#dcfce7' : '#fef3c7', color: isCurrent ? '#15803d' : '#b45309',
            display: 'inline-flex', alignItems: 'center', gap: 4,
          }}>
            {isCurrent ? <><Check size={11} /> Current</> : '★ Popular'}
          </span>
        )}

        <div style={{ padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: '1.05rem', paddingRight: isCurrent || item.isPopular ? 78 : 0 }}>
            {item.displayName || item.name}
          </div>
          {item.packageType && (
            <div style={{ fontSize: '.72rem', fontWeight: 600, color: accent, textTransform: 'capitalize', marginTop: 2 }}>{item.packageType}</div>
          )}

          <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, margin: '10px 0 4px' }}>
            <span style={{ fontSize: '1.65rem', fontWeight: 800, color: accent }}>₹{fmt(item.price)}</span>
            <span style={{ fontSize: '.76rem', fontWeight: 500, color: 'var(--text-muted)' }}>
              {isCatv ? '/month' : ` / ${item.validityDays || 30} days`}
            </span>
          </div>

          {/* Headline spec — speed for broadband, channels for cable */}
          {!isCatv && item.speed && (
            <div style={{ fontSize: '.85rem', fontWeight: 600, marginBottom: 4 }}>
              ⬇ {item.speed.download} Mbps · ⬆ {item.speed.upload} Mbps
              {item.dataLimit ? ` · ${item.dataLimit} GB` : ' · Unlimited'}
            </div>
          )}
          {isCatv && (item.channelCount > 0 || item.hasIPTV) && (
            <div style={{ fontSize: '.85rem', fontWeight: 600, marginBottom: 4 }}>
              {item.channelCount > 0 && `${item.channelCount} Channels`}
              {item.hdChannelCount > 0 && ` · ${item.hdChannelCount} HD`}
              {item.hasIPTV && ` · IPTV${item.iptvChannelCount ? ` (${item.iptvChannelCount})` : ''}`}
            </div>
          )}
          {item.validityDays && (
            <div style={{ fontSize: '.75rem', color: 'var(--text-muted)', marginBottom: 8 }}>
              Valid {item.validityDays} days
            </div>
          )}

          {item.description && (
            <div style={{ fontSize: '.79rem', color: 'var(--text-muted)', marginBottom: 8 }}>{item.description}</div>
          )}

          {/* Genre / feature chips */}
          {(genres.length > 0 || item.features?.length > 0) && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
              {genres.map(g => (
                <span key={g} style={{ fontSize: '.7rem', fontWeight: 600, textTransform: 'capitalize', padding: '3px 9px', borderRadius: 99, background: `${accent}14`, color: accent }}>{g}</span>
              ))}
              {(item.features || []).map((f, i) => (
                <span key={i} style={{ fontSize: '.7rem', fontWeight: 600, padding: '3px 9px', borderRadius: 99, background: `${accent}14`, color: accent }}>{f}</span>
              ))}
            </div>
          )}

          {/* Multi-month bundle prices */}
          {item.pricingTiers?.length > 0 && (
            <div style={{ fontSize: '.74rem', color: 'var(--text-muted)', marginBottom: 10 }}>
              {item.pricingTiers.map(t => `${t.label} ₹${fmt(t.price)}`).join(' · ')}
            </div>
          )}

          {item.pdf && (
            <a href={item.pdf} target="_blank" rel="noreferrer" style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              marginBottom: 10, padding: '8px 0', borderRadius: 10, textDecoration: 'none',
              fontWeight: 600, fontSize: '.8rem', color: accent, border: `1.5px solid ${accent}33`,
            }}>
              <FileText size={13} /> View Plan PDF
            </a>
          )}

          <button
            onClick={() => handleChoose(item)}
            style={{
              marginTop: 'auto', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              padding: '10px 0', borderRadius: 12, cursor: 'pointer', fontWeight: 700, fontSize: '.85rem',
              background: isCurrent ? 'transparent' : accent, color: isCurrent ? accent : '#fff',
              border: isCurrent ? `1.5px solid ${accent}` : 'none',
            }}>
            <PenLine size={13} /> {isCurrent ? 'Renew this plan' : 'Choose this plan'}
          </button>
        </div>
      </div>
    );
  }

  // Broadband plan lists can run long — a searchable dropdown lets the customer jump
  // straight to a plan by typing instead of scrolling a card grid. Only the selected
  // plan's card renders below.
  const selectedBbId = selectedBbPlanId || currentId || items[0]?._id || '';
  const selectedBbItem = serviceType === 'broadband' ? items.find(p => p._id === selectedBbId) || null : null;

  return (
    <div>
      <h2 style={{ fontSize: '1.2rem', fontWeight: 700, marginBottom: 4 }}>Plans</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: '.85rem', marginBottom: 20 }}>
        Browse available plans and submit a request to switch or renew — our team confirms and applies it in next billing cycle.
      </p>

      {/* No CableTV/Broadband toggle for combo customers — business rule, 2026-07-27:
          they only browse/pay for Broadband here; a CableTV switch goes through support
          as a manual request instead. serviceType is forced to 'broadband' for them in
          the load effect above, so there's nothing to toggle. */}

      {confirmed ? (
        <div style={{ display: 'grid', gap: 16 }}>
          <div className="card" style={{ padding: 18, borderColor: '#bbf7d0' }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>Request submitted — Ticket {confirmed.ticketNumber}</div>
            <p style={{ fontSize: '.85rem', color: 'var(--text-muted)' }}>
              We've received your request for <strong>{confirmed.plan.displayName || confirmed.plan.name}</strong>.
              Pay below to speed up confirmation — our team will apply the plan once payment is verified.
            </p>
          </div>
          <PaymentStep
            amount={confirmed.plan.price}
            note={`Plan Change - ${account?.customerId || ''}`}
            config={serviceType === 'catv'
              ? { upiEntries: config.catvUpiEntries, upiName: config.catvUpiName }
              : { upiEntries: config.upiEntries, upiName: config.upiName }}
          />
        </div>
      ) : serviceType === 'broadband' ? (
        <div>
          {items.length === 0 ? (
            <div className="card empty-state" style={{ padding: 40 }}>
              <p>No plans available right now. Contact your admin.</p>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: 18, maxWidth: 420 }}>
                <SearchableSelect
                  options={items.map(p => ({ value: p._id, label: `${p.displayName || p.name} — ₹${fmt(p.price)}${p.speed ? ` · ${p.speed.download} Mbps` : ''}` }))}
                  value={selectedBbId}
                  onChange={setSelectedBbPlanId}
                  placeholder="Search broadband plans…"
                />
              </div>
              {selectedBbItem && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
                  {renderPlanCard(selectedBbItem)}
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          {items.length === 0 && (
            <div className="card empty-state" style={{ padding: 40, gridColumn: '1 / -1' }}>
              <p>No plans available right now. Contact your admin.</p>
            </div>
          )}
          {items.map(renderPlanCard)}
        </div>
      )}

      {signOpen && (
        <SignaturePad value={null} onSave={handleSign} onClose={() => setSignOpen(false)} />
      )}

      {dtdNoticeOpen && (
        <DtdPlanChangeNotice onClose={() => setDtdNoticeOpen(false)} />
      )}
    </div>
  );
}
